import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { TermsAndConditionsModal } from 'src/components/terms-and-conditions-modal';
import { setSessionStorageItem } from 'src/utils/session-storage';

import accessDeniedImage from '../../assets/access-denied.svg';
import notFoundImage from '../../assets/not-found.svg';
import { ErrorPage } from '../../components/error-page';
import { ViewerCustomAppearance } from '../../components/viewer-custom-appearance';
import { useDevTools } from '../../dev-tools';
import { SecurityPolicyType, useGetWebcastViewerQuery } from '../../generated/graphql-viewer';
import {
  findViewerAccessCodePolicy,
  findViewerEmailRegistrationPolicy,
  findViewerEmailWhitelistPolicy,
  findViewerReferrerProtectionPolicy,
  findViewerTokenProtectionPolicy,
} from '../../utils/graphql-helpers';
import { setUserInformation } from '../../utils/user-information-utils';
import { ViewerSpinner } from '../components/viewer-spinner';
import { useSsoAuthentication } from '../hooks/use-sso-authentication';
import { SelectedLanguageProvider } from '../providers/selected-language-provider';
import { ViewerAccessProvider } from '../providers/viewer-access-provider';
import { AccessForm } from '../views/access-form/access-form';
import { Stream } from '../views/stream';

import './viewer.css';

export function ViewerApp() {
  const { webcastId = '', language: urlLanguage } = useParams();
  const { applicationAccessGranted, ssoAccessDenied } = useSsoAuthentication();
  const { t } = useTranslation();
  const { mockedViewerState } = useDevTools();

  const [accessCode, setAccessCode] = useState<string>();
  const [email, setEmail] = useState<string>();

  const { data, loading } = useGetWebcastViewerQuery({ variables: { webcastId }, skip: !webcastId });
  const webcast = data?.webcast;
  const referrerProtectionPolicy = findViewerReferrerProtectionPolicy(webcast?.securityPolicies);
  const tokenProtectionPolicy = findViewerTokenProtectionPolicy(webcast?.securityPolicies);
  const protectionTokenAccessGranted = tokenProtectionPolicy?.accessGranted;

  const accessCodePolicy = findViewerAccessCodePolicy(webcast?.securityPolicies);
  const emailRegistrationPolicy = findViewerEmailRegistrationPolicy(webcast?.securityPolicies);
  const emailWhitelistPolicy = findViewerEmailWhitelistPolicy(webcast?.securityPolicies);

  const accessGranted =
    emailRegistrationPolicy?.accessGranted || accessCodePolicy?.accessGranted || emailWhitelistPolicy?.accessGranted;

  const accessFormVisible = webcast?.securityPolicies.some(({ type }) =>
    [
      SecurityPolicyType.ACCESS_CODE,
      SecurityPolicyType.EMAIL_REGISTRATION,
      SecurityPolicyType.EMAIL_WHITELIST,
    ].includes(type as SecurityPolicyType)
  );

  const handleAccessFormSubmit = (firstName: string, lastName: string, email: string) => {
    setEmail(email);
    setUserInformation(webcastId, { nickname: `${firstName} ${lastName}`.trim(), email });
  };

  const pageLoading =
    mockedViewerState === 'loading' ||
    loading ||
    applicationAccessGranted === undefined ||
    (!applicationAccessGranted && ssoAccessDenied === undefined);

  const eventNotFound = mockedViewerState === 'not-found' || !webcast;

  const accessDenied =
    mockedViewerState === 'access-denied' ||
    (referrerProtectionPolicy && !referrerProtectionPolicy.accessGranted) ||
    protectionTokenAccessGranted === false ||
    ssoAccessDenied;

  const accessRequired =
    mockedViewerState === 'access-code' ||
    mockedViewerState === 'email-registration' ||
    (!accessGranted && accessFormVisible);

  const getLocalizedTermsAndConditions = () => {
    const isMultiLanguages = Boolean(webcast?.additionalLangs?.length);
    const usedLanguage =
      (!isMultiLanguages && webcast?.primaryLang.code === 'de') || urlLanguage === 'de' ? 'de' : 'en';

    return (
      webcast?.termsAndConditions?.find((termsAndCondition) => termsAndCondition.locale === usedLanguage) ||
      webcast?.termsAndConditions?.find((termsAndCondition) => termsAndCondition.locale === 'en') ||
      webcast?.termsAndConditions?.[0]
    );
  };

  const termsAndConditions = getLocalizedTermsAndConditions();

  const showTermsAndConditions =
    termsAndConditions && sessionStorage.getItem('lspro-consumer-terms-and-conditions-consent') !== 'CONSENT_GIVEN';

  return (
    <ViewerAccessProvider email={email} accessCode={accessCode}>
      <SelectedLanguageProvider>
        <ViewerCustomAppearance />

        {pageLoading ? (
          <ViewerSpinner />
        ) : eventNotFound ? (
          <ErrorPage
            data-testid="not-found"
            title={t('viewer.notFound.title')}
            subTitle={t('viewer.notFound.subTitle')}
            image={notFoundImage}
          />
        ) : accessDenied ? (
          <ErrorPage
            data-testid="access-denied"
            title={t('viewer.accessDenied.title')}
            subTitle={t('viewer.accessDenied.subTitle')}
            image={accessDeniedImage}
          />
        ) : accessRequired ? (
          <AccessForm onAccessCodeChange={setAccessCode} onSubmit={handleAccessFormSubmit} />
        ) : (
          <>
            {showTermsAndConditions && (
              <TermsAndConditionsModal
                header={termsAndConditions.header}
                subheader={termsAndConditions.subheader}
                content={termsAndConditions.content}
                language={termsAndConditions.locale}
                onAcceptClick={() =>
                  setSessionStorageItem('lspro-consumer-terms-and-conditions-consent', 'CONSENT_GIVEN')
                }
              />
            )}
            <Stream />
          </>
        )}

        {/* TODO: LSPRO-2724 - Bring back the consent banner when requirements are specified */}
        {/* <ConsentBanner /> */}
      </SelectedLanguageProvider>
    </ViewerAccessProvider>
  );
}
